import { AlertCircle } from 'lucide-react';
import React, { useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

import {
  CreateProofreadingRule,
  ProofreadingRule,
} from '../../../services/types/proofreading-types';
import { useProofreadingRules } from '../hooks/useProofreadingRules';
import { RuleForm } from '../rules/RuleForm';
import { RuleList } from '../rules/RuleList';

interface RuleManagementProps {
  firmId: string;
}

export const RuleManagement: React.FC<RuleManagementProps> = ({ firmId }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentRule, setCurrentRule] = useState<ProofreadingRule | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { createRule, updateRule } = useProofreadingRules(firmId);

  const handleCreateClick = () => {
    setCurrentRule(null);
    setIsFormOpen(true);
  };

  const handleEditRule = (rule: ProofreadingRule) => {
    setCurrentRule(rule);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (formData: CreateProofreadingRule) => {
    try {
      setError(null);

      if (currentRule) {
        await updateRule(currentRule.id, formData);
      } else {
        await createRule(formData);
      }

      setIsFormOpen(false);
      setCurrentRule(null);
    } catch (err) {
      console.error('Failed to save rule:', err);
      setError('Failed to save rule. Please try again.');
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setCurrentRule(null);
    setError(null);
  };

  const isEditing = !!currentRule;

  return (
    <div className="container max-w-5xl mx-auto py-6">
      <RuleList
        firmId={firmId}
        onEditRule={handleEditRule}
        onCreateRuleClick={handleCreateClick}
      />

      <Sheet open={isFormOpen} onOpenChange={setIsFormOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>
              {isEditing ? 'Edit Rule' : 'Create New Rule'}
            </SheetTitle>
          </SheetHeader>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <RuleForm
            initialValues={currentRule || undefined}
            firmId={firmId}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
};
